import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from './supabaseClient';
import ClientForm from './ClientForm'; // Import the new form
import AddressManagerModal from './AddressManagerModal';
import toast, { Toaster } from 'react-hot-toast'; // Import react-hot-toast
import { useTenant } from './context/TenantContext';

const ClientManager = ({ userPermissions, user }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true); // For initial fetch
  const [isSaving, setIsSaving] = useState(false); // For add/edit operations
  const [isDeleting, setIsDeleting] = useState(false); // For delete operations
  const [error, setError] = useState(null); // For persistent fetch errors
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  // Stores the ID value exactly as it existed when Edit was clicked.
  // Used as the WHERE clause target so a user changing the ID field in the
  // form doesn't lose track of which row to update.
  const [originalClientId, setOriginalClientId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [addressModalClient, setAddressModalClient] = useState(null);
  const [isProcessingExcel, setIsProcessingExcel] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const { tenant } = useTenant();

  /* eslint-disable */
  const fetchClients = useCallback(async () => {
    if (!tenant) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.from('v_client_summary').select('*');

      if (error) throw error;
      setClients(data);
    } catch (err) {
      console.error("Error fetching client summary:", err);
      setError("Failed to load client data.");
      toast.error("Failed to load client data.");
    } finally {
      setLoading(false);
    }
  }, [tenant, user]);
  /* eslint-enable */

  const fetchAuditLogs = useCallback(async () => {
    setLoadingAuditLogs(true);
    setAuditError(null);
    try {
      const { data, error } = await supabase
        .from('migration_audit_logs')
        .select('*')
        .order('created_at', { ascending: false });

      // Step 1 diagnostic — log the full Supabase error so we can see
      // the exact code (e.g. 42P01 = table missing, 42703 = column missing,
      // PGRST301 = RLS blocked) rather than just the generic message.
      if (error) {
        console.error('[AuditLogs] Supabase error code:', error.code);
        console.error('[AuditLogs] Supabase error message:', error.message);
        console.error('[AuditLogs] Supabase error details:', error.details);
        console.error('[AuditLogs] Full error object:', error);
        throw error;
      }

      setAuditLogs(data ?? []);
    } catch (err) {
      console.error('[AuditLogs] Caught error in fetchAuditLogs:', err);
      // Show a soft error in the section only — do not crash the whole page
      setAuditError("Failed to load audit history. Check console for details.");
    } finally {
      setLoadingAuditLogs(false);
    }
  }, []);



  const triggerExcelMigration = useCallback(async () => {
    setIsProcessingExcel(true);
    try {
      const { data, error } = await supabase.rpc('trigger_excel_migration', {});
      if (error) throw error;
      const migratedCount = data?.count || 0;
      toast.success(`Standardized and migrated ${migratedCount} clients!`);
      fetchClients();
      fetchAuditLogs(); // Re-fetch audit logs after migration
    } catch (err) {
      console.error("Error triggering Excel migration:", err);
      toast.error('Failed to trigger Excel migration.');
    } finally {
      setIsProcessingExcel(false);
    }
  }, [fetchClients, fetchAuditLogs]);

  /* eslint-disable */
  useEffect(() => {
    if (!tenant) return;
    if (userPermissions?.canViewClientes) {
      fetchClients();
      fetchAuditLogs();
    }
    setCheckingPermissions(false);
  }, [tenant, fetchClients, fetchAuditLogs, userPermissions]);
  /* eslint-enable */

  const handleAdd = () => {
    setOriginalClientId(null);
    setSelectedClient(null);
    setIsFormOpen(true);
  };

  const handleEdit = (client) => {
    // Snapshot the ID at the moment Edit is clicked. This is the value that
    // will be used in the UPDATE .eq() clause even if the user changes the
    // ID field inside the form.
    setOriginalClientId(client.id);
    setSelectedClient(client);
    setIsFormOpen(true);
  };

  const handleDelete = async (clientId) => {
    if (window.confirm('Are you sure you want to delete this client? This action cannot be undone.')) {
      setIsDeleting(true);
      try {
        const { error } = await supabase
          .from('customers')
          .update({ deleted_at: new Date().toISOString() })
          .eq('id', clientId);

        if (error) throw error;
        toast.success('Client deleted successfully!');
        fetchClients(); // Refresh the list
      } catch (err) {
        console.error('Error deleting client:', err);
        toast.error('Failed to delete client.');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleSave = async (formData) => {
    setIsSaving(true);
    try {
      if (selectedClient) {
        // ── TASK 2: UPDATE ────────────────────────────────────────────────
        // Use originalClientId (snapshotted at edit-open time) as the WHERE
        // target so a user-changed ID field doesn't break the lookup.
        const updatePayload = {
          full_name: formData.full_name,
          phone_number: formData.phone_number,
          id: formData.id,
        };
        console.log('UPDATE payload:', updatePayload, '| WHERE id =', originalClientId);

        const { error: updateError } = await supabase
          .from('customers')
          .update(updatePayload)
          .eq('id', originalClientId);

        if (updateError) {
          console.error('Supabase UPDATE error (full):', updateError);
          throw updateError;
        }
      } else {
        // ── TASK 1: INSERT ────────────────────────────────────────────────
        // Pre-flight check: reject if the chosen ID already exists.
        const { data: existing, error: checkErr } = await supabase
          .from('customers')
          .select('id')
          .eq('id', formData.id)
          .is('deleted_at', null)
          .limit(1);

        if (checkErr) {
          console.error('ID pre-check error:', checkErr);
          throw checkErr;
        }

        if (existing && existing.length > 0) {
          toast.error('Este ID ya está en uso. Elegí otro.');
          return; // finally still fires → isSaving resets; catch is skipped
        }

        // Pre-flight check: reject if phone number already exists
        const { data: existingPhone, error: phoneCheckErr } = await supabase
          .from('customers')
          .select('id')
          .eq('phone_number', formData.phone_number)
          .is('deleted_at', null)
          .limit(1);

        if (phoneCheckErr) {
          console.error('Phone pre-check error:', phoneCheckErr);
          throw phoneCheckErr;
        }

        if (existingPhone && existingPhone.length > 0) {
          toast.error('Este número de teléfono ya está registrado. Usá otro número.');
          return;
        }

        const insertPayload = {
          id: formData.id,
          full_name: formData.full_name,
          phone_number: formData.phone_number,
        };
        console.log('INSERT payload:', insertPayload);

        const { error: insertError } = await supabase
          .from('customers')
          .insert([insertPayload]);

        if (insertError) {
          console.error('Supabase INSERT error (full):', insertError);
          throw insertError;
        }
      }

      // ── TASK 3: post-save ─────────────────────────────────────────────
      toast.success(`Client ${selectedClient ? 'updated' : 'added'} successfully!`);
      setIsFormOpen(false);
      fetchClients();
    } catch (err) {
      console.error('Error saving client (full error):', err);
      toast.error(`Failed to ${selectedClient ? 'update' : 'add'} client. ${err.message || ''}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Minimal client-type chip. C = casillero, E = encomienda, C·E = both.
  // Renders nothing for NONE/null so brand-new clients stay visually quiet.
  const typeChip = (t) => {
    const map = {
      CASILLERO:  { label: 'C',   title: 'Cliente casillero' },
      ENCOMIENDA: { label: 'E',   title: 'Cliente encomienda' },
      BOTH:       { label: 'C·E', title: 'Casillero y encomienda' },
    };
    const cfg = map[t];
    if (!cfg) return null;
    return (
      <span
        title={cfg.title}
        style={{
          display: 'inline-block',
          marginLeft: '6px',
          padding: '1px 5px',
          fontSize: '9px',
          fontWeight: 700,
          lineHeight: '14px',
          borderRadius: '3px',
          letterSpacing: '0.3px',
          border: '1px solid var(--border)',
          color: 'var(--text-secondary)',
          verticalAlign: 'middle',
          whiteSpace: 'nowrap',
        }}
      >
        {cfg.label}
      </span>
    );
  };

  const filteredClients = useMemo(() => {
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    return clients.filter(client =>
      (client.full_name || '').toLowerCase().includes(lowerCaseSearchTerm) ||
      (client.id || '').toLowerCase().includes(lowerCaseSearchTerm)
    );
  }, [clients, searchTerm]);


  if (checkingPermissions) {
    return <div className="p-4 text-center">Checking permissions...</div>;
  }

  if (!userPermissions?.canViewClientes) {
    return <div className="p-4 text-center text-red-500">Access Denied: You must be an administrator to view this page.</div>;
  }

  if (loading) return <div className="p-4 text-center">Loading client data...</div>;
  if (error) return <div className="p-4 text-center text-red-500">{error}</div>;

  return (
    <div className="space-y-6">
      <Toaster /> {/* Toast container */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Gestor de Clientes</h1>
          <p className="page-subtitle">Administración de base de datos de consignatarios</p>
        </div>
        <div className="header-actions">
          {userPermissions?.canEditClientes && (
            <button
              onClick={handleAdd}
              className="btn btn-primary"
              style={{ background: 'var(--green)' }}
              disabled={isSaving || isDeleting || isProcessingExcel}
            >
              {isSaving ? 'Añadiendo...' : 'Nuevo Cliente'}
            </button>
          )}
          <button
            onClick={fetchClients}
            className="btn btn-secondary"
            disabled={loading || isSaving || isDeleting || isProcessingExcel}
          >
            {loading ? 'Refrescando...' : 'Refrescar'}
          </button>
          {userPermissions?.canEditClientes && (
            <button
              onClick={triggerExcelMigration}
              className="btn btn-secondary"
              style={{ color: 'var(--amber)', borderColor: 'var(--amber)' }}
              disabled={isProcessingExcel || loading || isSaving || isDeleting}
            >
              {isProcessingExcel ? 'Procesando...' : 'Procesar Importación Excel'}
            </button>
          )}
        </div>
      </div>

      <div className="tile" style={{ padding: '16px' }}>
        <input
          type="text"
          placeholder="Buscar por Nombre o ID..."
          className="input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={loading || isSaving || isDeleting || isProcessingExcel}
        />
      </div>

      {isFormOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
          <div className="tile w-full max-w-md animate-in zoom-in-95">
            <ClientForm
              client={selectedClient}
              onSave={handleSave}
              onCancel={() => setIsFormOpen(false)}
              isSaving={isSaving} // Pass saving state to form
            />
          </div>
        </div>
      )}

      {addressModalClient && (
        <AddressManagerModal
          client={addressModalClient}
          userPermissions={userPermissions}
          onClose={() => setAddressModalClient(null)}
        />
      )}

      {isDeleting && <div className="badge badge-error w-full text-center p-2 mb-4">Eliminando cliente...</div>}

      {filteredClients.length === 0 ? (
        <p className="empty-state">No se encontraron clientes.</p>
      ) : (
        <div className="tile" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
                  <th className="px-6 py-3 text-left metric-label">ID</th>
                  <th className="px-6 py-3 text-left metric-label">Nombre</th>
                  <th className="px-6 py-3 text-left metric-label">Teléfono</th>
                  <th className="px-6 py-3 text-left metric-label">Paquetes</th>
                  <th className="px-6 py-3 text-center metric-label">Direcciones</th>
                  <th className="px-6 py-3 text-center metric-label">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'var(--border)' }}>
                {filteredClients.map((client) => (
                  <tr
                    key={client.id}
                    className="hover:bg-gray-50/50 transition"
                    style={client.package_count > 0 ? { background: 'var(--blue-soft)' } : {}}
                  >
                    <td className="px-6 py-3 row-id" style={{ width: 'auto' }}>{client.id || '—'}{typeChip(client.client_type)}</td>
                    <td className="px-6 py-3 text-sm font-medium" style={{ color: 'var(--text-main)' }}>{client.full_name || '—'}</td>
                    <td className="px-6 py-3 text-sm" style={{ color: 'var(--text-secondary)' }}>{client.phone_number || '—'}</td>
                    <td className="px-6 py-3">
                      <span className={`badge ${client.package_count > 0 ? 'badge-done' : 'badge-pending'}`}>
                        {client.package_count ?? 0}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-center">
                      <button
                        onClick={() => setAddressModalClient(client)}
                        className="btn btn-secondary"
                        style={{ padding: '4px 8px', fontSize: '10px', color: 'var(--blue-text)', borderColor: 'var(--blue)' }}
                      >
                        📍 Ver
                      </button>
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center justify-center gap-2">
                        {userPermissions?.canEditClientes && (
                          <button
                            onClick={() => handleEdit(client)}
                            className="btn btn-secondary"
                            style={{ padding: '4px 8px', fontSize: '10px' }}
                            disabled={isSaving || isDeleting || isProcessingExcel}
                          >
                            Editar
                          </button>
                        )}
                        {userPermissions?.canEditClientes && (
                          <button
                            onClick={() => handleDelete(client.id)}
                            className="btn btn-ghost"
                            style={{ padding: '4px 8px', fontSize: '10px', color: 'var(--avianca-red)' }}
                            disabled={isSaving || isDeleting || isProcessingExcel}
                          >
                            Eliminar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="pt-8">
        <h2 className="page-title mb-4">Historial de Importaciones</h2>
        {loadingAuditLogs ? (
          <div className="empty-state">Cargando historial...</div>
        ) : auditError ? (
          <div className="badge badge-error w-full text-center p-3">{auditError}</div>
        ) : auditLogs.length === 0 ? (
          <p className="empty-state">No hay historial de importaciones.</p>
        ) : (
          <div className="tile" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
                    <th className="px-6 py-3 text-left metric-label">Fecha</th>
                    <th className="px-6 py-3 text-left metric-label">Clientes Migrados</th>
                    <th className="px-6 py-3 text-left metric-label">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: 'var(--border)' }}>
                  {auditLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50/50 transition">
                      <td className="px-6 py-3 text-sm" style={{ color: 'var(--text-secondary)' }}>{new Date(log.created_at).toLocaleString()}</td>
                      <td className="px-6 py-3 text-sm font-bold" style={{ color: 'var(--text-main)' }}>{log.migrated_count}</td>
                      <td className="px-6 py-3">
                        <span className={`badge ${
                          log.status === 'success' ? 'badge-done' :
                          log.status === 'failed' ? 'badge-error' :
                          'badge-pending'
                        }`}>
                          {log.status.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientManager;
